/**
 * «Tu bot está listo» — el cierre del alta (GKTIBO-126).
 *
 * Reemplaza a `PasoDeRevision`. La diferencia no es cosmética: aquél existía
 * para llegar a **salir en vivo**, y por eso trataba la falta de canal como un
 * paso incompleto del alta. Éste existe para decir dos cosas a la vez, y las
 * dos son ciertas:
 *
 * 1. **Tu bot ya sabe atender.** Cargaste lo que sabe, te lo preguntó Tibo, y
 *    lo probaste. Eso está hecho y es lo que costó trabajo.
 * 2. **Todavía nadie puede escribirle.** Falta conectar un canal.
 *
 * ## Por qué se avisa una vez y no se insiste
 *
 * Decisión del PO. Un banner que vuelve en cada carga es lo que convierte un
 * producto en algo que molesta: el dueño ya lo sabe, y si no lo hizo es porque
 * no tiene a mano los datos de WhatsApp Business. Después de esta pantalla
 * queda un indicador discreto en el panel y nada más.
 *
 * Las dos salidas van **al mismo nivel**: «Conectar ahora» y «Más tarde». Un
 * botón grande y un enlace escondido no son una elección, son un empujón.
 */

import type { RespuestaDeOnboarding } from '@chatbot/shared-types';
import { CheckCircle2, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { derivarEstado } from './EstadoDelAlta';
import { textos } from './textos';

interface TuBotEstaListoProps {
  estado: RespuestaDeOnboarding;
  /** Pone el tenant en `active`. Si tira, se muestra el error y se reintenta. */
  activar: () => Promise<void>;
}

export function TuBotEstaListo({ estado, activar }: TuBotEstaListoProps) {
  const [confirmando, setConfirmando] = useState(false);
  const [activando, setActivando] = useState(false);
  const [fallo, setFallo] = useState(false);
  const [enVivo, setEnVivo] = useState(false);

  const derivado = derivarEstado(estado);
  const t = textos.listo;

  async function confirmar() {
    setActivando(true);
    setFallo(false);
    try {
      await activar();
      setEnVivo(true);
      setConfirmando(false);
    } catch {
      setFallo(true);
      setConfirmando(false);
    } finally {
      setActivando(false);
    }
  }

  return (
    <section className="alta-paso alta-listo" data-testid="alta-listo">
      <div className="alta-listo-corona" aria-hidden="true">
        <CheckCircle2 size={28} />
      </div>

      <h2 className="card-title">{enVivo ? t.tituloEnVivo : t.titulo}</h2>
      <p className="alta-paso-ayuda">{enVivo ? t.bajadaEnVivo : t.bajada}</p>

      {/*
        El aviso del canal. Aparece SÓLO si falta, y con las dos salidas a la
        vista. Si ya hay canal conectado, esta pantalla es puro botón de salir
        en vivo y no hay nada que avisar.
      */}
      {!enVivo && derivado.faltaCanal && (
        <div className="alta-listo-falta-canal" data-testid="alta-falta-canal">
          <TriangleAlert size={20} aria-hidden="true" className="alta-listo-falta-icono" />
          <div className="alta-listo-falta-cuerpo">
            <strong>{t.nadiePuedeEscribirle}</strong>
            <p>{t.faltaCanal}</p>
            <div className="alta-listo-salidas">
              <Link className="boton-como-enlace" to="/configuracion/canales">
                {t.conectarAhora}
              </Link>
              <Link className="alta-listo-mas-tarde" to="/">
                {t.masTarde}
              </Link>
            </div>
          </div>
        </div>
      )}

      {fallo && (
        <div className="alta-pie-error" role="alert">
          <p>{t.noSePudoActivar}</p>
        </div>
      )}

      {/*
        Salir en vivo sigue exigiendo canal, y eso no es política nuestra: sin
        canal nadie puede escribirle. Por eso el botón se muestra sólo cuando
        el servidor dice que se puede — y cuando no, arriba ya está dicho por
        qué, así que no queda un botón gris y callado (C7, L34).
      */}
      {!enVivo && derivado.puedeActivar && (
        <Button variant="primary" onClick={() => setConfirmando(true)}>
          {t.activar}
        </Button>
      )}

      {!enVivo && !derivado.puedeActivar && !derivado.faltaCanal && (
        <p className="alta-paso-ayuda" data-testid="alta-listo-otra-razon">
          {derivado.pasos.find((p) => p.id === 'listo')?.falta}
        </p>
      )}

      {enVivo && (
        <Link className="boton-como-enlace" to="/">
          {t.irAlPanel}
        </Link>
      )}

      <Modal
        open={confirmando}
        onOpenChange={setConfirmando}
        title={textos.revision.confirmarTitulo}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmando(false)}>
              {textos.revision.confirmarVuelva}
            </Button>
            <Button variant="primary" onClick={confirmar} loading={activando}>
              {activando ? textos.revision.activando : textos.revision.confirmarSiga}
            </Button>
          </>
        }
      >
        {/* Dice QUÉ VA A PASAR. «¿Estás seguro?» no informa nada: el dueño ya
            sabe que apretó un botón; lo que no sabe es qué desencadena. */}
        <p>{textos.revision.confirmarQuePasa}</p>
      </Modal>
    </section>
  );
}
